<template>
    <div class="min-h-screen flex flex-col bg-gray-100">
        <!-- Mobile Menu Button -->
        <div class="bg-white p-4 border-b border-gray-200 flex justify-end md:hidden">
            <button @click="isSidebarOpen = !isSidebarOpen" class="text-gray-600 text-2xl">
                <font-awesome-icon :icon="isSidebarOpen ? 'times' : 'bars'" />
            </button>
        </div>

        <div class="flex flex-1">
            <!-- Sidebar Navigation -->
            <aside
                :class="[isSidebarOpen ? 'block' : 'hidden', 'md:block', 'w-64 bg-white border-r border-gray-200 shadow-md transition-all duration-300 ease-in-out']">
                <div class="p-6 text-center bg-gray-50">
                    <h2 class="text-xl font-semibold text-gray-800">{{ $translate('legal_pages') }}</h2>
                    <p class="text-sm text-gray-500">{{ $translate('please_select_a_document') }}</p>
                </div>

                <nav class="mt-6">
                    <ul class="space-y-2">
                        <li @click="setActiveSection('terms')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'terms' }">
                            <span class="nav-icon"><font-awesome-icon icon="file-contract" /></span>
                            {{ $translate('terms_of_service') }}
                        </li>
                        <li @click="setActiveSection('privacy')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'privacy' }">
                            <span class="nav-icon"><font-awesome-icon icon="user-secret" /></span>
                            {{ $translate('privacy_policy') }}
                        </li>
                        <li @click="setActiveSection('cookiePolicy')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'cookiePolicy' }">
                            <span class="nav-icon"><font-awesome-icon icon="cookie-bite" /></span>
                            {{ $translate('cookie_policy') }}
                        </li>
                        <li @click="setActiveSection('contact')" class="nav-item"
                            :class="{ 'nav-item-active': activeSection === 'contact' }">
                            <span class="nav-icon"><font-awesome-icon icon="envelope" /></span>
                            {{ $translate('contact_us') }}
                        </li>
                    </ul>
                </nav>

                <!-- Back to Event List Button -->
                <div class="mt-auto p-6">
                    <button @click="handleHome"
                        class="w-full bg-blue-600 text-white py-2 px-4 rounded-lg shadow-md hover:bg-blue-700 transition duration-300">
                        {{ $translate('back_to_event_list') }}
                    </button>
                </div>
            </aside>

            <!-- Main content area -->
            <main class="flex-1 p-6 bg-gray-50 shadow-sm rounded-lg">
                <component :is="activeSectionComponent"></component>
            </main>
        </div>
    </div>
</template>

<script>
import TermsPage from './pages/TermsPage.vue';
import PrivacyPage from './pages/PrivacyPage.vue';
import CookiePolicyPage from './pages/CookiePolicyPage.vue';
import ContactPage from './pages/ContactPage.vue';
import { getSectionFromPath, getSystemPath } from '@/utils/helper';

export default {
    name: 'LegalPages',
    components: {
        TermsPage,
        PrivacyPage,
        CookiePolicyPage,
        ContactPage,
    },
    data() {
        return {
            activeSection: 'terms', // Default section
            isSidebarOpen: false, // Sidebar toggle state
        };
    },
    computed: {
        activeSectionComponent() {
            switch (this.activeSection) {
                case 'privacy':
                    return 'PrivacyPage';
                case 'contact':
                    return 'ContactPage';
                case 'cookiePolicy':
                    return 'CookiePolicyPage';
                default:
                    return 'TermsPage';
            }
        },
    },
    created() {
        this.setActiveSectionFromRoute(); // Set active section based on route
    },
    watch: {
        '$route'() {
            this.setActiveSectionFromRoute(); // Watch for route changes
        },
    },
    methods: {
        setActiveSectionFromRoute() {
            const section = getSectionFromPath(this.$route.params.id);
            if (section) {
                this.activeSection = section;
            } else {
                this.activeSection = 'terms'; // Default to Terms
            }
        },
        setActiveSection(section) {
            this.activeSection = section;
            this.isSidebarOpen = false; // Close sidebar on mobile
            const sectionName = getSystemPath(section); // Use helper to get the system path

            // Update the route with the selected section
            this.$router.push({ name: 'LegalPages', params: { id: sectionName } });
        },
        handleHome() {
            this.$router.push({ name: 'EventList' });
        },
    },
};
</script>

<style scoped>
.nav-item {
    display: flex;
    align-items: center;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    color: #4b5563;
    border-left: 4px solid transparent;
    transition: background-color 0.3s, color 0.3s, border-color 0.3s;
}

.nav-item:hover {
    background-color: #f3f4f6;
    color: #1f2937;
}

.nav-item-active {
    background-color: #e5f3ff;
    color: #2563eb;
    border-left-color: #2563eb;
}

.nav-icon {
    width: 1.5rem;
    display: flex;
    justify-content: center;
    margin-right: 1rem;
}

button {
    cursor: pointer;
}

@media (min-width: 768px) {
    aside {
        display: block !important;
    }
}
</style>
